/**
 * Surveys — every staff read and write goes through the Hono `surveys` router
 * (`apps/api/src/routes/people/surveys.ts`, `/api/v1/surveys`, flag-gated on
 * `surveys`, area-gated on the `surveys` org scope). The anonymous answer page
 * (`/s/:token`) uses the PUBLIC router (`/api/v1/public/surveys/:token`) via
 * `publicGet` / `publicPost` — never with a staff session attached.
 *
 * The wire shapes are the pinned contract in `@releve/shared`
 * `schemas/surveysReads.ts`; the write bodies are `schemas/surveys.ts`
 * (`.strict()` — send only the schema's keys).
 *
 * Query keys, all under the `['surveys', orgId]` prefix, so ONE invalidation
 * after a write refreshes every survey surface:
 * - index      `['surveys', orgId, 'index', {}]` (via `indexQueryOptions`)
 * - detail     `['surveys', orgId, 'detail', surveyId]`
 * - responses  `['surveys', orgId, 'responses', surveyId]` — ONE query shared
 *              by the Results and the Responses tabs.
 *
 * Errors: a translated toast. The coded refusals (`survey_frozen`,
 * `survey_not_open`, `survey_invalid_answers`) are user-facing rules declared
 * in `meta.expectedErrorCodes`, so the global `MutationCache.onError`
 * (App.tsx) keeps Sentry quiet for them. Answers are NEVER attached to a
 * toast description, a breadcrumb or Sentry extra.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SURVEY_ERROR_CODES,
  isAnswerableQuestion,
  type CreateSurveyInput,
  type PublicSurveyResponse,
  type PublicSurveySubmitResponse,
  type SurveyAnswers,
  type SurveyDeleteResponse,
  type SurveyDetailResponse,
  type SurveyIndexRowWire,
  type SurveyResponseDeleteResponse,
  type SurveyResponseRowWire,
  type SurveyResponsesResponse,
  type SurveyResponseWriteResponse,
  type SurveyStatus,
  type SurveyWire,
  type SurveyWriteResponse,
  type UpdateSurveyInput,
} from '@releve/shared';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import {
  ApiError,
  authedDelete,
  authedGet,
  authedPatch,
  authedPost,
  publicGet,
  publicPost,
} from '@/lib/api';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';
import { trackEvent } from '@/lib/posthog';

/* ---------------------------------- keys --------------------------------- */

export const surveyKeys = {
  all: ['surveys'] as const,
  org: (orgId: string | undefined) => ['surveys', orgId] as const,
  detail: (orgId: string | undefined, surveyId: string | undefined) =>
    ['surveys', orgId, 'detail', surveyId] as const,
  responses: (orgId: string | undefined, surveyId: string | undefined) =>
    ['surveys', orgId, 'responses', surveyId] as const,
  /** The public page — not org-scoped (the token is the credential). */
  publicSurvey: (token: string | undefined) => ['public-survey', token] as const,
};

const surveysBase = (orgId: string) => `/api/v1/surveys/orgs/${orgId}`;
const publicBase = '/api/v1/public/surveys';

const DETAIL_STALE_MS = 30 * 1000;

function requireOrg(orgId: string | undefined): string {
  if (!orgId) throw new Error('No organization selected');
  return orgId;
}

/* ------------------------------ coded errors ----------------------------- */

/** The coded envelope of a failed survey request (`err.envelope.code`), or `null`. */
export function surveyErrorCodeOf(error: unknown): string | null {
  const code = (error as { envelope?: { code?: unknown } } | null | undefined)?.envelope?.code;
  return typeof code === 'string' ? code : null;
}

export const SURVEY_MUTATION_META = {
  expectedErrorCodes: Object.values(SURVEY_ERROR_CODES) as readonly string[],
} as const;

const CODED_TOAST_KEYS: Record<string, string> = {
  [SURVEY_ERROR_CODES.frozen]: 'surveys:errors.survey_frozen',
  [SURVEY_ERROR_CODES.notOpen]: 'surveys:errors.survey_not_open',
  [SURVEY_ERROR_CODES.invalidAnswers]: 'surveys:errors.survey_invalid_answers',
  [SURVEY_ERROR_CODES.emptyResponse]: 'surveys:errors.survey_empty_response',
  [SURVEY_ERROR_CODES.notFound]: 'surveys:errors.not_found',
};

/**
 * Toast a failed survey write: a coded refusal or a 404 gets its own
 * message, anything else the `failedKey` headline with the server message.
 */
export function toastSurveyError(error: Error, failedKey: string) {
  const code = surveyErrorCodeOf(error);
  if (code && CODED_TOAST_KEYS[code]) {
    toast.error(i18n.t(CODED_TOAST_KEYS[code]));
    return;
  }
  if (error instanceof ApiError && error.status === 404) {
    toast.error(i18n.t('surveys:errors.not_found'));
    return;
  }
  toast.error(i18n.t(failedKey), error.message ? { description: error.message } : undefined);
}

/* --------------------------------- queries ------------------------------- */

/**
 * The COMPLETE org survey index (`survey_index` view) for People → Surveys.
 * Search and sort run client-side. Liveness: every writer below invalidates
 * `['surveys', orgId]`; a new public response moves `response_count` /
 * `last_response_at` and is covered by the 60 s staleTime.
 */
export function useSurveyIndex() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery(
    indexQueryOptions<SurveyIndexRowWire>({
      entity: 'surveys',
      organizationId: orgId,
      path: `${surveysBase(orgId ?? '')}/index`,
    }),
  );
}

/** One survey with its questions. A 404 resolves to `null`. */
export function useSurveyDetail(surveyId: string | undefined, options?: { enabled?: boolean }) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery({
    queryKey: surveyKeys.detail(orgId, surveyId),
    queryFn: async (): Promise<SurveyDetailResponse | null> => {
      try {
        return await authedGet<SurveyDetailResponse>(`${surveysBase(orgId!)}/surveys/${surveyId}`);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: !!orgId && !!surveyId && (options?.enabled ?? true),
    staleTime: DETAIL_STALE_MS,
  });
}

/**
 * Resolve a survey CODE (the detail route's `:code`) to its index row, then
 * load the detail by id. `isLoading` covers both steps; `survey` is `null`
 * once the code is known not to exist.
 *
 * A code missing from the cached index while the index is REFETCHING is still
 * loading, not "not found": Create and Duplicate invalidate the index and
 * navigate straight to the new code, so the cached (stale) index lacks it
 * until the in-flight refetch lands.
 */
export function useSurveyByCode(code: string | undefined) {
  const index = useSurveyIndex();
  const row = code ? index.data?.find((r) => r.code === code) : undefined;
  const detail = useSurveyDetail(row?.id, { enabled: !!row });
  const isLoading =
    index.isLoading || (!row && index.isFetching) || (!!row && detail.isLoading);
  return {
    survey: isLoading ? undefined : (detail.data?.survey ?? null),
    isLoading,
    isError: index.isError || detail.isError,
  };
}

/**
 * Every response of a survey, newest first. The API pages past the
 * PostgREST 1,000-row cap (`fetchAllKeyset`); the Results tab summarizes the
 * same cached list (`summarizeSurvey`), so both tabs share this one query.
 */
export function useSurveyResponses(surveyId: string | undefined) {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  return useQuery({
    queryKey: surveyKeys.responses(orgId, surveyId),
    queryFn: async (): Promise<SurveyResponseRowWire[]> => {
      const result = await authedGet<SurveyResponsesResponse>(
        `${surveysBase(orgId!)}/surveys/${surveyId}/responses`,
      );
      return result.items;
    },
    enabled: !!orgId && !!surveyId,
    staleTime: DETAIL_STALE_MS,
  });
}

/* -------------------------------- mutations ------------------------------ */

/** `survey_created` (ids-free): shape facts of the saved survey, never its text. */
function trackSurveyCreated(source: 'new' | 'duplicate', survey: SurveyWire): void {
  trackEvent('survey_created', {
    source,
    question_count: survey.questions.filter(isAnswerableQuestion).length,
    display_mode: survey.display_mode,
    has_project: survey.project_id !== null,
  });
}

function useInvalidateSurveys() {
  const queryClient = useQueryClient();
  return (orgId: string | undefined) => {
    void queryClient.invalidateQueries({ queryKey: surveyKeys.org(orgId) });
  };
}

/** Create a draft survey. Resolves to the saved survey. */
export function useCreateSurvey() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const invalidate = useInvalidateSurveys();
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: (input: CreateSurveyInput) =>
      authedPost<SurveyWriteResponse>(`${surveysBase(requireOrg(orgId))}/surveys`, input),
    onSuccess: (result) => {
      invalidate(orgId);
      toast.success(i18n.t('surveys:toast.created', { code: result.survey.code }));
      trackSurveyCreated('new', result.survey);
    },
    onError: (error: Error) => toastSurveyError(error, 'surveys:toast.createFailed'),
  });
}

export interface UpdateSurveyVariables {
  surveyId: string;
  /** Presence-keyed; `questions` only while the survey is a draft (else 409 `survey_frozen`). */
  patch: UpdateSurveyInput;
}

/** Edit a survey (PATCH). */
export function useUpdateSurvey() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const invalidate = useInvalidateSurveys();
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: ({ surveyId, patch }: UpdateSurveyVariables) =>
      authedPatch<SurveyWriteResponse>(`${surveysBase(requireOrg(orgId))}/surveys/${surveyId}`, patch),
    onSuccess: (result) => {
      invalidate(orgId);
      toast.success(i18n.t('surveys:toast.updated', { code: result.survey.code }));
    },
    onError: (error: Error) => toastSurveyError(error, 'surveys:toast.updateFailed'),
  });
}

export interface SurveyStatusVariables {
  surveyId: string;
  status: Exclude<SurveyStatus, 'draft'>;
}

/** Open, close or reopen a survey. Never back to draft. */
export function useSetSurveyStatus() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const invalidate = useInvalidateSurveys();
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: ({ surveyId, status }: SurveyStatusVariables) =>
      authedPost<SurveyWriteResponse>(`${surveysBase(requireOrg(orgId))}/surveys/${surveyId}/status`, {
        status,
      }),
    onSuccess: (result) => {
      invalidate(orgId);
      toast.success(
        i18n.t(result.survey.status === 'open' ? 'surveys:toast.opened' : 'surveys:toast.closed', {
          code: result.survey.code,
        }),
      );
      // The route never returns a draft (no way back to draft); the guard narrows the type.
      if (result.survey.status !== 'draft') {
        trackEvent('survey_status_changed', { status: result.survey.status });
      }
    },
    onError: (error: Error) => toastSurveyError(error, 'surveys:toast.statusFailed'),
  });
}

/** Replace the public link (the old one stops working at once). */
export function useRegenerateSurveyLink() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const invalidate = useInvalidateSurveys();
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: (surveyId: string) =>
      authedPost<SurveyWriteResponse>(
        `${surveysBase(requireOrg(orgId))}/surveys/${surveyId}/regenerate-link`,
        {},
      ),
    onSuccess: () => {
      invalidate(orgId);
      toast.success(i18n.t('surveys:toast.linkRegenerated'));
    },
    onError: (error: Error) => toastSurveyError(error, 'surveys:toast.linkFailed'),
  });
}

/** Copy a survey into a new draft. Resolves to the copy. */
export function useDuplicateSurvey() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const invalidate = useInvalidateSurveys();
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: (surveyId: string) =>
      authedPost<SurveyWriteResponse>(`${surveysBase(requireOrg(orgId))}/surveys/${surveyId}/duplicate`, {}),
    onSuccess: (result) => {
      invalidate(orgId);
      toast.success(i18n.t('surveys:toast.duplicated', { code: result.survey.code }));
      trackSurveyCreated('duplicate', result.survey);
    },
    onError: (error: Error) => toastSurveyError(error, 'surveys:toast.duplicateFailed'),
  });
}

/** Delete a survey and all its responses. Resolves to the deleted id. */
export function useDeleteSurvey() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const invalidate = useInvalidateSurveys();
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: async (surveyId: string): Promise<string> => {
      const result = await authedDelete<SurveyDeleteResponse>(`${surveysBase(requireOrg(orgId))}/surveys/${surveyId}`);
      return result.id;
    },
    onSuccess: (surveyId) => {
      queryClient.removeQueries({ queryKey: surveyKeys.detail(orgId, surveyId) });
      queryClient.removeQueries({ queryKey: surveyKeys.responses(orgId, surveyId) });
      invalidate(orgId);
      toast.success(i18n.t('surveys:toast.deleted'));
    },
    onError: (error: Error) => toastSurveyError(error, 'surveys:toast.deleteFailed'),
  });
}

export interface AddSurveyResponseVariables {
  surveyId: string;
  answers: SurveyAnswers;
}

/** Key in a paper response (`source = 'staff'`). Only while the survey is open. */
export function useAddSurveyResponse() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const invalidate = useInvalidateSurveys();
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: ({ surveyId, answers }: AddSurveyResponseVariables) =>
      authedPost<SurveyResponseWriteResponse>(`${surveysBase(requireOrg(orgId))}/surveys/${surveyId}/responses`, {
        answers,
      }),
    onSuccess: () => {
      invalidate(orgId);
      toast.success(i18n.t('surveys:toast.responseAdded'));
    },
    onError: (error: Error) => toastSurveyError(error, 'surveys:toast.responseFailed'),
  });
}

export interface DeleteSurveyResponseVariables {
  surveyId: string;
  responseId: string;
}

/** Remove one response (spam, a duplicate paper form). */
export function useDeleteSurveyResponse() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const invalidate = useInvalidateSurveys();
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: ({ surveyId, responseId }: DeleteSurveyResponseVariables) =>
      authedDelete<SurveyResponseDeleteResponse>(
        `${surveysBase(requireOrg(orgId))}/surveys/${surveyId}/responses/${responseId}`,
      ),
    onSuccess: () => {
      invalidate(orgId);
      toast.success(i18n.t('surveys:toast.responseDeleted'));
    },
    onError: (error: Error) => toastSurveyError(error, 'surveys:toast.responseDeleteFailed'),
  });
}

/* ------------------------------- public page ----------------------------- */

/**
 * The public survey behind `token`: the whole `PublicSurveyResponse` — an
 * open survey (`{ survey }`) or a closed one's branding only (`{ closed }`;
 * discriminate with `'closed' in data`). The API answers a UNIFORM 404 for an
 * unknown or draft token — resolved here to `null` (one plain "not
 * available" state, no existence oracle). No retries on 404 / 429.
 */
export function usePublicSurvey(token: string | undefined) {
  return useQuery({
    queryKey: surveyKeys.publicSurvey(token),
    queryFn: async () => {
      try {
        return await publicGet<PublicSurveyResponse>(`${publicBase}/${encodeURIComponent(token!)}`);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    enabled: !!token,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && (error.status === 429 || error.status < 500)) && failureCount < 2,
  });
}

export interface SubmitPublicSurveyVariables {
  answers: SurveyAnswers;
  /** The honeypot value — whatever the hidden field holds (normally ''). */
  website: string;
}

/** Submit an anonymous response. The page owns the error UI (no toast, no Sentry from here). */
export function useSubmitPublicSurvey(token: string | undefined) {
  return useMutation({
    meta: SURVEY_MUTATION_META,
    mutationFn: ({ answers, website }: SubmitPublicSurveyVariables) =>
      publicPost<PublicSurveySubmitResponse>(`${publicBase}/${encodeURIComponent(token ?? '')}`, {
        answers,
        ...(website ? { website } : {}),
      }),
  });
}
